import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/slices/authSlice";

export const useTour = () => {
  const user = useSelector(selectUser);
  const [tourDone, setTourDone] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setTourDone(false);
      return;
    }

    const subscriptionStatus = user?.company?.subscription_status?.toLowerCase();
    const isSubscriptionActiveOrTrial =
      subscriptionStatus === "active" || subscriptionStatus?.includes("trial");

    const isKycVerified =
      user?.kyc_verification === true || user?.company?.kyc_verification === true;

    if (!isSubscriptionActiveOrTrial || !isKycVerified) {
      setTourDone(true);
      return;
    }

    const tourKey = `fleetrify_tour_done_${user.id}`;
    const hasSeenTour = localStorage.getItem(tourKey);
    if (hasSeenTour) {
      setTourDone(true);
      return; // stops here if already seen
    }

    const style = document.createElement("style");
    style.innerHTML = `
      // .driver-overlay {
      //   z-index: 30 !important;
      // }
      .driver-popover {
        // z-index: 31 !important;
        background: #ffffff !important;
        border-radius: 16px !important;
        padding: 20px 24px !important;
        box-shadow: 0 20px 60px rgba(20, 56, 135, 0.15) !important;
        border: 1px solid #e8edf7 !important;
        min-width: 280px !important;
        max-width: 320px !important;
      }
      .driver-popover-title {
        font-size: 15px !important;
        font-weight: 700 !important;
        color: #143887 !important;
        margin-bottom: 8px !important;
      }
      .driver-popover-description {
        font-size: 13px !important;
        color: #4b5563 !important;
        line-height: 1.6 !important;
      }
      .driver-popover-progress-text {
        font-size: 11px !important;
        color: #9ca3af !important;
        font-weight: 500 !important;
      }
      .driver-popover-footer {
        margin-top: 16px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 8px !important;
      }
      .driver-popover-prev-btn,
      .driver-popover-next-btn,
      .driver-popover-done-btn {
        background: #143887 !important;
        color: #ffffff !important;
        border: none !important;
        border-radius: 8px !important;
        padding: 8px 16px !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        cursor: pointer !important;
      }
      .driver-popover-prev-btn {
        background: #f1f4fb !important;
        color: #143887 !important;
      }
      .driver-popover-prev-btn:hover { background: #e2e8f5 !important; }
      .driver-popover-next-btn:hover,
      .driver-popover-done-btn:hover { background: #0f2d6e !important; }
      .driver-popover-close-btn {
        color: #9ca3af !important;
        font-size: 18px !important;
      }
      .driver-popover-close-btn:hover { color: #143887 !important; }
    `;
    document.head.appendChild(style);

    // Child elements that only appear when their parent dropdown is open.
    // Always included in validSteps — they'll be in the DOM when the tour reaches them
    // because we programmatically open the dropdown just before those steps.
    const DROPDOWN_CHILD_ELEMENTS = new Set([
      "#staff-menu",
      "#orders-menu",
      "#relocation-menu",
    ]);

    const allSteps = [
      {
        popover: {
          title: "👋 Welcome to Fleetrify!",
          description:
            "Let's take a quick tour so you can get started with confidence. Click Next to begin.",
          side: "over" as const,
          align: "center" as const,
        },
      },
      {
        element: "#topnav-search",
        popover: {
          title: "🔍 Search",
          description:
            "Quickly find anything across the platform — orders, staff, vehicles and more.",
          side: "bottom" as const,
          align: "start" as const,
        },
      },
      {
        element: "#topnav-video-guide",
        popover: {
          title: "🎬 Video Guide",
          description:
            "New here? Watch short walkthrough videos that explain how to use each feature of Fleetrify.",
          side: "bottom" as const,
          align: "end" as const,
        },
      },
      {
        element: "#topnav-notifications",
        popover: {
          title: "🔔 Notifications",
          description:
            "Stay up to date. Get alerts for new orders, assignments, messages and system updates in real time.",
          side: "bottom" as const,
          align: "end" as const,
        },
      },
      {
        element: "#topnav-profile",
        popover: {
          title: "👤 Your Profile",
          description:
            "View and update your account details, profile picture and role. You can also log out from here.",
          side: "bottom" as const,
          align: "end" as const,
        },
      },
      {
        element: "#dashboard-menu",
        popover: {
          title: "🏠 Dashboard",
          description:
            "Your command center. See real-time fleet activity, key metrics and performance at a glance.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        element: "#branch-menu",
        popover: {
          title: "🏢 Branch",
          description:
            "Manage all your company branches, assign staff to locations and monitor branch operations.",
          side: "right" as const,
          align: "start" as const,
        },
      },

      // ── Users / Staff ───────────────────────────────────────────────────────
      // Step 1: parent button (dropdown closed — introduces the section)
      {
        element: "#users-menu",
        popover: {
          title: "👥 Users",
          description:
            "This section contains your team management tools. Click Next and we'll open it for you.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      // Step 2: Staff child (dropdown will be open when we reach this)
      {
        element: "#staff-menu",
        popover: {
          title: "👤 Staff",
          description:
            "View and manage all your staff members. Assign roles and branches, activate or deactivate accounts, and keep your team organised.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      // Step 3: Invite tip — anchored to the same Staff link while dropdown is still open
      {
        element: "#staff-menu",
        popover: {
          title: "📧 Invite Staff",
          description:
            "To add a new team member, go to the Staff page and click the Invitations tab. Then click 'Add New Staff' to send an email invite. You can track pending and declined invites from that same view.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      // ────────────────────────────────────────────────────────────────────────

      // ── Order Management ────────────────────────────────────────────────────
      // Step 1: parent button (dropdown closed — introduces the section)
      {
        element: "#order-management-menu",
        popover: {
          title: "📦 Order Management",
          description:
            "This is your hub for all customer order activity. It contains two sub-modules — click Next and we'll open them for you.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      // Step 2: Orders child (dropdown will be open by the time we reach this)
      {
        element: "#orders-menu",
        popover: {
          title: "📋 Orders",
          description:
            "View and manage all standard delivery and logistics orders. Track status in real time, assign drivers, update progress and review full order history.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      // Step 3: Relocation Requests child
      {
        element: "#relocation-menu",
        popover: {
          title: "🚛 Relocation Requests",
          description:
            "Manage customer relocation requests here. Review incoming requests, negotiate pricing through counter-offers, and accept or reject offers directly from the detail view.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      // ────────────────────────────────────────────────────────────────────────

      {
        element: "#fleet-management-menu",
        popover: {
          title: "🚚 Fleet Management",
          description:
            "Track and manage your fleet assets, vehicles, and driver assignments.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        element: "#message-menu",
        popover: {
          title: "💬 Messages",
          description:
            "Communicate with your team members directly from the platform in real time.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        element: "#service-menu",
        popover: {
          title: "🔧 Services",
          description:
            "Define and manage services your fleet offers. Track requests, assignments and completion.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        element: "#wallet-menu",
        popover: {
          title: "💰 Wallet",
          description:
            "View your balance, fund your wallet and make secure withdrawals. All transactions are tracked.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        element: "#ai-insights-menu",
        popover: {
          title: "🤖 AI Insights",
          description:
            "Get AI-generated insights and recommendations based on your business activity.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        element: "#settings-menu",
        popover: {
          title: "⚙️ Settings",
          description:
            "Configure your account, notification preferences, roles and platform settings here.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        element: "#logout-btn",
        popover: {
          title: "🚪 Log Out",
          description:
            "Click here to securely end your session and log out of Fleetrify.",
          side: "right" as const,
          align: "start" as const,
        },
      },
      {
        popover: {
          title: "🎉 You're all set!",
          description:
            "You now know your way around Fleetrify. Explore each module and reach out to support anytime. Happy managing!",
          side: "over" as const,
          align: "center" as const,
        },
      },
    ];

    // Always include dropdown children — they'll be in the DOM when the tour reaches them
    const validSteps = allSteps.filter((step) => {
      if (!("element" in step)) return true;
      if (DROPDOWN_CHILD_ELEMENTS.has((step as any).element)) return true;
      return !!document.querySelector((step as any).element);
    });

    // ── Compute step indices for dropdown orchestration ──────────────────────
    // Users group: usersIdx → staffIdx → staffInviteIdx (consecutive)
    const usersIdx = validSteps.findIndex(
      (s) => "element" in s && (s as any).element === "#users-menu"
    );
    const staffIdx    = usersIdx >= 0 ? usersIdx + 1 : -1;
    const staffInviteIdx = usersIdx >= 0 ? usersIdx + 2 : -1;

    // Order Management group: orderMgmtIdx → ordersIdx → relocationIdx (consecutive)
    const orderMgmtIdx = validSteps.findIndex(
      (s) => "element" in s && (s as any).element === "#order-management-menu"
    );
    const ordersIdx     = orderMgmtIdx >= 0 ? orderMgmtIdx + 1 : -1;
    const relocationIdx = orderMgmtIdx >= 0 ? orderMgmtIdx + 2 : -1;
    // ────────────────────────────────────────────────────────────────────────

    const clickElement = (selector: string) =>
      document.querySelector<HTMLElement>(selector)?.click();

    const closeUsersDropdown = () => {
      if (document.querySelector("#staff-menu")) clickElement("#users-menu");
    };
    const closeOrderDropdown = () => {
      if (document.querySelector("#orders-menu")) clickElement("#order-management-menu");
    };

    let usersDropdownOpen = false;
    let orderDropdownOpen = false;

    const driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayOpacity: 0.5,
      stagePadding: 8,
      stageRadius: 12,
      progressText: "{{current}} of {{total}}",
      nextBtnText: "Next →",
      prevBtnText: "← Back",
      doneBtnText: "Done 🎉",
      steps: validSteps,

      onNextClick: () => {
        const idx = driverObj.getActiveIndex() ?? -1;

        // Users dropdown — open when leaving the Users intro step
        if (idx === usersIdx) {
          clickElement("#users-menu");
          usersDropdownOpen = true;
          setTimeout(() => driverObj.moveNext(), 350);
          return;
        }

        // Users dropdown — close after the Invite tip step
        if (idx === staffInviteIdx) {
          closeUsersDropdown();
          usersDropdownOpen = false;
        }

        // Order dropdown — open when leaving the Order Management intro step
        if (idx === orderMgmtIdx) {
          clickElement("#order-management-menu");
          orderDropdownOpen = true;
          setTimeout(() => driverObj.moveNext(), 350);
          return;
        }

        // Order dropdown — close after the Relocation step
        if (idx === relocationIdx) {
          closeOrderDropdown();
          orderDropdownOpen = false;
        }

        driverObj.moveNext();
      },

      onPrevClick: () => {
        const idx = driverObj.getActiveIndex() ?? -1;

        // Going back from Staff → Users: close users dropdown first
        if (idx === staffIdx) {
          closeUsersDropdown();
          usersDropdownOpen = false;
          setTimeout(() => driverObj.movePrevious(), 350);
          return;
        }

        // Going back from Orders → Order Management: close order dropdown first
        if (idx === ordersIdx) {
          closeOrderDropdown();
          orderDropdownOpen = false;
          setTimeout(() => driverObj.movePrevious(), 350);
          return;
        }

        driverObj.movePrevious();
      },

      onDestroyStarted: () => {
        // Close any dropdowns the tour opened before marking complete
        if (usersDropdownOpen) { closeUsersDropdown(); usersDropdownOpen = false; }
        if (orderDropdownOpen) { closeOrderDropdown(); orderDropdownOpen = false; }
        localStorage.setItem(tourKey, "true");
        setTourDone(true);
        driverObj.destroy();
      },
    });

    const timer = setTimeout(() => {
      driverObj.drive();
    }, 800);

    return () => {
      clearTimeout(timer);
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [
    user?.id,
    user?.company?.subscription_status,
    user?.kyc_verification,
    user?.company?.kyc_verification,
  ]); // only re-runs if user changes

  return { tourDone };
};